import { useMemo, useCallback } from 'react';

// Track last fetch times for each cache key
const fetchTimestamps = new Map<string, number>();

// Common fetch policies that work with both regular and suspense queries
type CommonFetchPolicy = 'cache-first' | 'cache-and-network' | 'network-only';

interface UseCacheTTLOptions {
  cacheKey: string;
  ttlSeconds?: number;
  defaultPolicy?: CommonFetchPolicy;
}

/**
 * Hook to manage cache TTL for Apollo queries
 * Returns an object that can be spread directly into query options
 * Works with both regular queries and suspense queries
 *
 * @example
 * const cacheOptions = useCacheTTL({ cacheKey: 'my-query-key', ttlSeconds: 15 });
 * const { data } = useQuery(MY_QUERY, {
 *   variables: { ... },
 *   ...cacheOptions
 * });
 */
export default function useCacheTtl({
  cacheKey,
  ttlSeconds = 15,
  defaultPolicy = 'cache-and-network'
}: UseCacheTTLOptions) {
  const fetchPolicy = useMemo((): CommonFetchPolicy => {
    const lastFetch = fetchTimestamps.get(cacheKey);
    const now = Date.now();
    const ttlMs = ttlSeconds * 1000; // Convert seconds to milliseconds

    if (!lastFetch || (now - lastFetch) > ttlMs) {
      // Data is stale or doesn't exist, fetch from network and update timestamp
      fetchTimestamps.set(cacheKey, now);
      return defaultPolicy;
    } else {
      // Data is fresh, use cache only
      return 'cache-first';
    }
  }, [cacheKey, ttlSeconds, defaultPolicy]);

  // Still provide onCompleted for compatibility with regular queries
  const onCompleted = useCallback(() => {
    fetchTimestamps.set(cacheKey, Date.now());
  }, [cacheKey]);

  return { fetchPolicy, onCompleted };
}
